import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Modal,
} from 'components';
import { Margin } from 'styled-components-spacing';
import checkPermissions from '../../../helpers/checkPermissions';
import {
  DownloadIcon,
  File,
  FileHistoryTable,
  FileHistoryTd,
  FileHistoryTh,
  ToUploadList,
} from '../ChangeRequest.styled';
import { iconList } from '../../../assets';

const getProfileTypeLabel = (type) => {
  switch (type) {
    case 'late_upload':
      return 'Late upload';
    case 'change_in_cis':
      return 'Change in CIS';
    case 'invoice':
      return 'Invoice';
    default:
      return type || '-';
  }
};

const ViewChangeRequest = ({
  profile,
  loading,
  loadDetails,
  approveChangeRequest,
  userPermissions,
  type,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  let typeLabel = getProfileTypeLabel(profile?.type);

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    newStatus: null,
  });
  const [isActionInProgress, setIsActionInProgress] = useState(false);

  const fileHistory = (profile?.fileHistory || [])
    .slice()
    .sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));

  const fileHistoryActionLabel = (action) => {
    if (action === 'added') return 'added';
    if (action === 'removed') return 'removed';
    if (action === 'changed') return 'changed';
    return action || 'changed';
  };

  const formatDate = (value) => (value ? moment(value).format('DD/MM/YYYY HH:mm') : '-');

  const statusHistory = profile
    ? [
        {
          status: 'Pending',
          at: profile.createdAt,
          by: profile.createdBy,
        },
        profile.verificationAt && {
          status: profile.verificationResult || 'Verified / Refuted',
          at: profile.verificationAt,
          by: profile.verificationBy,
        },
        profile.decisionAt && {
          status: profile.decisionResult || 'Approved / Rejected',
          at: profile.decisionAt,
          by: profile.decisionBy,
        },
        profile.implementedAt && {
          status: 'Implemented',
          at: profile.implementedAt,
          by: profile.implementedBy,
        },
      ].filter((entry) => entry && entry.at)
    : [];

  const isIpma = type === 'ipma';
  const isCoo = checkPermissions({ roles: ['ipma_approve_change_request'] }, userPermissions || []);
  const canApproveReject = isIpma && isCoo && profile?.status === 'Pending';

  useEffect(() => {
    if (profile?.id) {
      loadDetails(profile.id, history);
    }
  }, [profile?.id, loadDetails, history]);

  const handleOpenConfirmModal = (newStatus) => {
    setConfirmModal({ isOpen: true, newStatus });
  };

  const handleCloseConfirmModal = () => {
    setConfirmModal({ isOpen: false, newStatus: null });
  };

  const handleConfirmAction = async () => {
    if (approveChangeRequest && profile?.id && confirmModal.newStatus) {
      setIsActionInProgress(true);
      await approveChangeRequest(profile.id, { status: confirmModal.newStatus });
      if (profile?.id) {
        await loadDetails(profile.id, history);
      }
      setIsActionInProgress(false);
      handleCloseConfirmModal();
      history.goBack();
    }
  };

  if (loading) {
    return (
      <PageContent>
        <Container>
          <p>{t('global.loading')}</p>
        </Container>
      </PageContent>
    );
  }

  if (!profile || !profile.id) {
    return (
      <PageContent>
        <Container>
          <p>{t('global.noData')}</p>
        </Container>
      </PageContent>
    );
  }

  return (
    <PageContent>
      <Container>
        <Margin bottom="24">
          <Heading size="h5">
            {t('page.user.cis.changeRequests.viewChangeRequest')}
          </Heading>
        </Margin>
        <Row>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="status"
                label={t('page.user.cis.changeRequests.status')}
              >
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  {profile.status || '-'}
                </div>
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field id="type" label="Type">
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  {typeLabel || '-'}
                </div>
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="createdAt"
                label={t('page.user.cis.changeRequests.createdAt')}
              >
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  {profile.createdAt && moment(profile.createdAt).format('DD/MM/YYYY HH:mm')}
                </div>
              </Field>
            </Margin>
          </Col>
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Field
                id="createdBy"
                label={t('page.user.cis.changeRequests.createdBy')}
              >
                <div style={{ padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  {profile.createdBy || '-'}
                </div>
              </Field>
            </Margin>
          </Col>
        </Row>
        <Row>
          <Col size={{ xl: 1 / 2 }}>
            <Margin bottom="20">
              <Field
                id="description"
                label={t('page.user.cis.changeRequests.description')}
              >
                <textarea readOnly rows={10}>
                  {profile.description || '-'}
                </textarea>
              </Field>
            </Margin>
          </Col>
        </Row>
        {profile.files && profile.files.length > 0 && (
          <Row>
            <Col size={{ xl: 1 }}>
              <Margin bottom="20">
                <Field
                  id="files"
                  label={t('page.user.cis.changeRequests.files')}
                >
                  <ToUploadList>
                      {profile.files.map((file, index) => (
                        <File key={`${file.fileName}-${index + 1}`}>
                          {file.fileUrl && (
                            <DownloadIcon
                              src={iconList.fileUpload}
                              title="Download file"
                              onClick={() => window.open(file.fileUrl, '_blank')}
                            />
                          )}
                          {file.fileName || `File ${index + 1}`}
                        </File>
                      ))}
                  </ToUploadList>
                </Field>
              </Margin>
            </Col>
          </Row>
        )}
        {statusHistory.length > 0 && (
          <Row>
            <Col size={{ xl: 1 }}>
              <Margin bottom="20">
                <Field id="statusHistory" label={t('page.user.cis.changeRequests.statusHistory')}>
                  <FileHistoryTable>
                    <thead>
                      <tr>
                        <FileHistoryTh>{t('page.user.cis.changeRequests.status')}</FileHistoryTh>
                        <FileHistoryTh>{t('page.user.cis.changeRequests.changedAt')}</FileHistoryTh>
                        <FileHistoryTh>{t('page.user.cis.changeRequests.changedBy')}</FileHistoryTh>
                      </tr>
                    </thead>
                    <tbody>
                      {statusHistory.map((entry) => (
                        <tr key={`status-${entry.status}-${entry.at}`}>
                          <FileHistoryTd>{entry.status}</FileHistoryTd>
                          <FileHistoryTd>{formatDate(entry.at)}</FileHistoryTd>
                          <FileHistoryTd>{entry.by || '-'}</FileHistoryTd>
                        </tr>
                      ))}
                    </tbody>
                  </FileHistoryTable>
                </Field>
              </Margin>
            </Col>
          </Row>
        )}
        {fileHistory.length > 0 && (
          <Row>
            <Col size={{ xl: 1 }}>
              <Margin bottom="20">
                <Field id="fileHistory" label="File history">
                  <FileHistoryTable>
                    <thead>
                      <tr>
                        <FileHistoryTh>Date</FileHistoryTh>
                        <FileHistoryTh>User</FileHistoryTh>
                        <FileHistoryTh>Change</FileHistoryTh>
                        <FileHistoryTh>File</FileHistoryTh>
                      </tr>
                    </thead>
                    <tbody>
                      {fileHistory.map((entry) => {
                        const name = entry?.fileName || entry?.filePath || '-';
                        const by = entry?.createdByUser?.fullName || entry?.createdBy || '-';
                        const at = entry?.createdAt ? moment(entry.createdAt).format('DD/MM/YYYY HH:mm') : '-';
                        const rawAction = fileHistoryActionLabel(entry?.action);
                        const action = rawAction.charAt(0).toUpperCase() + rawAction.slice(1);

                        return (
                          <tr key={entry?.id || `${entry?.action}-${entry?.createdAt}-${name}`}>
                            <FileHistoryTd>{at}</FileHistoryTd>
                            <FileHistoryTd>{by}</FileHistoryTd>
                            <FileHistoryTd>{action}</FileHistoryTd>
                            <FileHistoryTd>{name}</FileHistoryTd>
                          </tr>
                        );
                      })}
                    </tbody>
                  </FileHistoryTable>
                </Field>
              </Margin>
            </Col>
          </Row>
        )}
        <Row>
          {canApproveReject && (
            <>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Button
                  onClick={() => handleOpenConfirmModal('Approved')}
                  type="button"
                  style={{ marginRight: '10px' }}
                >
                  {t('page.user.cis.changeRequests.approve')}
                </Button>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Button
                  onClick={() => handleOpenConfirmModal('Rejected')}
                  type="button"
                >
                  {t('page.user.cis.changeRequests.reject')}
                </Button>
              </Margin>
            </Col>
            </>
          )}
          <Col size={{ xl: 1 / 3 }}>
            <Margin bottom="20">
              <Button secondary onClick={() => history.goBack()} type="button">
                {t('global.back')}
              </Button>
            </Margin>
          </Col>
        </Row>
        <Modal
          heading={
            confirmModal.newStatus === 'Approved'
              ? t('page.user.cis.changeRequests.confirmApproveTitle')
              : t('page.user.cis.changeRequests.confirmRejectTitle')
          }
          isOpen={confirmModal.isOpen}
          confirm={handleConfirmAction}
          cancel={handleCloseConfirmModal}
          onRequestClose={handleCloseConfirmModal}
          disabled={isActionInProgress}
          description={
            confirmModal.newStatus === 'Approved'
              ? t('page.user.cis.changeRequests.confirmApproveText')
              : t('page.user.cis.changeRequests.confirmRejectText')
          }
        />
      </Container>
    </PageContent>
  );
};

ViewChangeRequest.propTypes = {
  profile: PropTypes.object,
  loading: PropTypes.bool,
  loadDetails: PropTypes.func.isRequired,
  approveChangeRequest: PropTypes.func,
  userPermissions: PropTypes.array,
  type: PropTypes.string,
};

ViewChangeRequest.defaultProps = {
  profile: {},
  loading: false,
  approveChangeRequest: null,
  userPermissions: [],
  type: 'ipma',
};

export default ViewChangeRequest;
